import {announce} from '@github-ui/aria-live'
import {srOnlyHeader} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import type {CopilotChatMessage, CopilotChatReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {CheckIcon, LinkIcon, RepoForkedIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {useEffect} from 'react'

import {useOnGitHubFileLinkClick} from '../../hooks/use-on-github-file-link-click'
import chatMessageStyles from '../ChatMessage.module.css'
import {ChatMessageReferences} from '../ChatMessageReferences'
import {makeVersionedItemFromReference} from '../ContentPreview/content-preview-types'
import {useContentPreview} from '../ContentPreview/ContentPreviewContext'
import {MarkdownViewer} from '../MarkdownViewer'
import type {TimelineEvent, TimelineEventContent, TimelineEventTextReference} from './timeline-events-types'
import styles from './TimelineEvents.module.css'

function isTimelineReference(ref: CopilotChatReference): ref is TimelineEventTextReference {
  return ref.type === 'text' && ref.name != null && ref.name.startsWith('timeline-event:')
}

export function userMessageIsTimelineEvent(message: CopilotChatMessage, events?: TimelineEvent[]): boolean {
  return (
    message.references?.some(
      ref =>
        isTimelineReference(ref) &&
        (events === undefined || events?.some(event => ref.name.startsWith(`timeline-event: {"type": "${event}"`))),
    ) ?? false
  )
}

export type TimelineEventsProps = {
  message: CopilotChatMessage
  messageIndex: number
  isViewingSharedThread: boolean
  isSharedMessage?: boolean
  isLatestMessage?: boolean
}
export function TimelineEvents({
  message,
  messageIndex,
  isViewingSharedThread,
  isSharedMessage,
  isLatestMessage,
}: TimelineEventsProps) {
  /*
    There are two types of timeline events: a user message, and a situational message.

    - User messages: messages with specific reference names are timeline events. These messages don't
      go through the normal ChatMessage component; instead, we render them here as a timeline event.

    - Situational messages: some messages trigger a timeline event if they meet certain critera (ex: a shared message)
  */
  return (
    <>
      {userMessageIsTimelineEvent(message) && (
        <UserMessageTimelineEvent message={message} messageIndex={messageIndex} />
      )}
      <SituationalTimelineEvents
        isViewingSharedThread={isViewingSharedThread}
        isSharedMessage={isSharedMessage}
        isLatestMessage={isLatestMessage}
      />
    </>
  )
}

type UserMessageTimelineEventProps = {
  message: CopilotChatMessage
  messageIndex: number
}
function UserMessageTimelineEvent({message, messageIndex}: UserMessageTimelineEventProps) {
  // Certain references are used for versioning (Files, Issues, etc.)
  // Usually, processing these references happens in the ChatMessage, but for UserMessageTimelineEvents,
  // we don't render the ChatMessage. So, make sure these references / versions are processed:
  const {updateItem} = useContentPreview()
  const onGitHubLinkClick = useOnGitHubFileLinkClick()

  useEffect(() => {
    if (message.references) {
      for (const ref of message.references) {
        const item = makeVersionedItemFromReference({
          ref,
          messageIndex,
          messageId: message.id,
          timestamp: message.createdAt,
        })
        if (item != null) updateItem(item)
      }
    }
  }, [message.references, message.id, messageIndex, message.createdAt, updateItem])

  const timelineReferences = message.references?.filter(ref => isTimelineReference(ref))

  // Parse timeline events from references
  const timelineEvents: TimelineEventContent[] = []
  if (timelineReferences) {
    for (const timelineReference of timelineReferences) {
      const content = JSON.parse(timelineReference.name.replace('timeline-event: ', ''))
      const icon = timelineReference.icon || CheckIcon
      timelineEvents.push({type: content.type, markdownContent: content.markdownContent, icon})
    }
  }

  // Announce timeline events to screen readers
  // These are dynamically triggered by user actions (e.g., updating an issue, creating an issue)
  useEffect(() => {
    // Only announce if this message was created client-side (not loaded from history).
    // Messages loaded from the server have clientSide explicitly set to false.
    if (timelineEvents.length > 0 && message.clientSide) {
      // Get the plain text content from markdown by removing markdown link syntax
      // The format is always [text](url) from instructionsMarkdownContent(), so a simple regex is safe
      // e.g., "Updated issue [owner/repo#123](url)" -> "Updated issue owner/repo#123"
      for (const event of timelineEvents) {
        const plainText = event.markdownContent.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        announce(plainText)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message.id])

  if (!timelineReferences) return

  return (
    <>
      {message.references && message.references.length > 0 && (
        <ChatMessageReferences
          className={clsx(chatMessageStyles.references, styles.references)}
          messageId={message.id}
          messageTimestamp={message.createdAt}
          messageIndex={messageIndex}
          references={message.references}
          size="medium"
        />
      )}

      {timelineEvents.map(({type, markdownContent, icon: Icon}: TimelineEventContent) => {
        return (
          <div className={styles.timelineEvent} key={type}>
            <Icon className={styles.icon} />
            <MarkdownViewer
              autoOpenPreviewPane={false}
              markdown={markdownContent}
              messageId={message.id}
              messageIndex={messageIndex}
              messageTimestamp={message.createdAt}
              onGitHubLinkClick={onGitHubLinkClick}
              accessibleHeader={srOnlyHeader('Timeline', message.content ?? '')}
            />
          </div>
        )
      })}
    </>
  )
}

type SituationalTimelineEventsProps = {
  isSharedMessage?: boolean
  isViewingSharedThread?: boolean
  isLatestMessage?: boolean
}
function SituationalTimelineEvents({
  isSharedMessage,
  isViewingSharedThread,
  isLatestMessage,
}: SituationalTimelineEventsProps) {
  return (
    <>
      {isSharedMessage && !isViewingSharedThread && (
        <div className={styles.situationalTimelineEvent}>
          <LinkIcon className={styles.icon} /> Messages up to this point are included in shared link
        </div>
      )}

      {isLatestMessage && copilotFeatureFlags.copilotDuplicateThread && isViewingSharedThread && (
        <div className={styles.situationalTimelineEvent}>
          <RepoForkedIcon className={styles.icon} /> Messages beyond this point will start a new private chat
        </div>
      )}
    </>
  )
}
